import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { apiClient } from '@cast/infrastructure-api';

const NAMESPACES_DATA_TRANSFER_TOTAL_COSTS_QUERY_KEY =
  'namespaces-data-transfer-total-costs';

const getNamespacesDataTransferTotalCostsQueryKey = ({
  startTime,
  endTime,
  clusterId,
  pageLimit,
}: Partial<Props> = {}) =>
  [
    NAMESPACES_DATA_TRANSFER_TOTAL_COSTS_QUERY_KEY,
    startTime,
    endTime,
    clusterId,
    pageLimit,
  ] as const;

type Props = {
  startTime: string;
  endTime: string;
  clusterId: string;
  pageLimit?: number;
  enabled?: boolean;
};

const getNamespacesDataTransferTotalCostsQueryOptions = ({
  startTime,
  endTime,
  clusterId,
  pageLimit = 5,
  enabled = true,
}: Props) =>
  infiniteQueryOptions({
    queryKey: getNamespacesDataTransferTotalCostsQueryKey({
      startTime,
      endTime,
      clusterId,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await apiClient.costReport.getNamespacesDataTransferTotalCosts({
          startTime,
          endTime,
          clusterId,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
        });

      return data;
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useNamespacesDataTransferTotalCostsQuery = (props: Props) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getNamespacesDataTransferTotalCostsQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.items ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
    noDataReason: data?.pages[0].noDataReason ?? '',
  };
};
